import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bookmark } from "@/lib/icons/messaging-icons";
import { asksQuery } from "@/lib/messaging/notices/asks";
import { savedQuery } from "@/lib/messaging/history/saved";
import { draftsQuery } from "@/lib/messaging/compose/drafts";
import { scheduledQuery } from "@/lib/messaging/compose/scheduled";
import { useNow } from "../meetings/-components/meeting/use-now";
import { LaterSavedList } from "../-components/later/later-saved-list";
import { LaterAsksList } from "../-components/later/later-asks-list";
import { LaterDraftsList } from "../-components/later/later-drafts-list";
import { LaterScheduledList } from "../-components/later/later-scheduled-list";
import { InboxBackButton } from "../-components/home/inbox-back-button";

type Tab = "saved" | "asks" | "scheduled" | "drafts";
const TAB_NAMES: Record<Tab, string> = {
  saved: "Saved",
  asks: "Asked",
  scheduled: "Scheduled",
  drafts: "Drafts",
};
const isTab = (value: unknown): value is Exclude<Tab, "saved"> =>
  value === "asks" || value === "scheduled" || value === "drafts";

export const Route = createFileRoute("/_authenticated/messaging/_inbox/later")({
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => ({
    tab: isTab(search.tab) ? search.tab : undefined,
  }),
  component: LaterPage,
});

/** What the person kept to come back to, what they wrote to send later, and what they left
 * unsent. */
function LaterPage() {
  const { tab = "saved" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const now = new Date(useNow());
  const saved = useQuery(savedQuery());
  const asks = useQuery(asksQuery());
  const scheduled = useQuery(scheduledQuery());
  const drafts = useQuery(draftsQuery());
  const counts: Record<Tab, number> = {
    saved: saved.data?.items.length ?? 0,
    asks: asks.data?.items.filter((item) => item.status !== "done").length ?? 0,
    scheduled: scheduled.data?.scheduled.length ?? 0,
    drafts: drafts.data?.length ?? 0,
  };
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-2 border-b px-3 py-2 md:px-4">
        <InboxBackButton />
        <Bookmark className="text-muted-foreground size-4" />
        <h1 className="text-sm font-semibold">Later</h1>
      </header>
      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({
            search: { tab: isTab(value) ? value : undefined },
            replace: true,
          })
        }
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="border-b px-4 py-2">
          <TabsList>
            {(["saved", "asks", "scheduled", "drafts"] as const).map((value) => (
              <TabsTrigger key={value} value={value}>
                {TAB_NAMES[value]}
                {counts[value] > 0 ? (
                  <Badge variant="secondary" className="ms-1.5 tabular-nums">
                    {counts[value]}
                  </Badge>
                ) : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent value="saved" className="min-h-0 flex-1 overflow-y-auto">
          <LaterSavedList now={now} />
        </TabsContent>
        <TabsContent value="asks" className="min-h-0 flex-1 overflow-y-auto">
          <LaterAsksList now={now} />
        </TabsContent>
        <TabsContent value="scheduled" className="min-h-0 flex-1 overflow-y-auto">
          <LaterScheduledList now={now} />
        </TabsContent>
        <TabsContent value="drafts" className="min-h-0 flex-1 overflow-y-auto">
          <LaterDraftsList now={now} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
